import {
  notificationsService,
  parseUnreadCount,
  parseNotificationList,
  isFailedListResponse,
  isSuccessfulMutation,
} from '../services/notifications.service';
import { cookieDecryption } from '../../utils';
import { resolveNotificationIdentity } from '../../utils/notificationCountIdentity';

/**
 * Who the app is signed in as at this instant.
 *
 * Read from the cookie rather than from an effect's rootState, which is the
 * snapshot taken before the request was awaited and so still describes the
 * session that asked. Switching to an associated account happens in the same
 * tab: the cookie is replaced and the store is RESET, which puts feedRequestId
 * back to 0, the very value a request already in flight captured. The epoch
 * guard therefore cannot tell the two sessions apart, and a response that
 * outlived its session would paint the previous account's notifications, and
 * its unread count, into the account that replaced it.
 */
const currentNotificationIdentity = () => resolveNotificationIdentity(cookieDecryption('userDetails'));

/** Rows per dropdown page. Matches what the socket implementation requested,
 * and stays under the backend's hard ceiling of 100. */
const FEED_PAGE_SIZE = 10;

/**
 * Concatenates two pages of notifications, dropping any row from `second` whose
 * `_id` is already present. Order is preserved: `first` wins both position and
 * content, which is what makes a refresh put new rows on top and a load-more
 * put them at the bottom.
 */
const dedupeById = (first, second) => {
  const seen = new Set(first.map(item => String(item?._id)));

  return [
    ...first,
    ...second.filter(item => {
      const id = String(item?._id);

      if (seen.has(id)) {
        return false;
      }

      seen.add(id);

      return true;
    }),
  ];
};

/**
 * Notification Feed v2 state.
 */
const notificationsModel = {
  state: {
    unreadCount: 0,
    unreadCountError: false,
    /**
     * In-flight guard for the badge count. The socket `getCounts` event
     * triggers a refetch, and a user can receive several notifications at once,
     * so without this a burst would fire a request per event.
     */
    isUnreadCountLoading: false,
    unreadCountRefetchQueued: false,
    feed: [],
    feedPage: 1,
    feedPages: 0,
    isFeedLoading: false,
    isFeedLoadingMore: false,
    feedError: false,
    /**
     * True while the tray is open. A socket event only refreshes the list when
     * someone is looking at it -- refetching a closed tray would be pure waste,
     * and it reopens from a fresh page 1 anyway.
     */
    isFeedOpen: false,
    /**
     * Bumped by resetFeed. A feed request carries the value it saw when it
     * started, and the write reducers drop the result if it no longer matches —
     * i.e. the dropdown was closed while the request was in flight. Without it a
     * late response repopulates a closed dropdown, and a late load-more can
     * overwrite a newer page 1.
     */
    feedRequestId: 0,
  },
  reducers: {
    setData(state, payload) {
      return { ...state, ...payload };
    },

    /**
     * Commits a feed page. Lives in a reducer rather than the effect for two
     * reasons: it must see the CURRENT feed (an effect's rootState is the
     * snapshot from dispatch time, taken before the request was awaited), and it
     * must be able to reject a result whose requestId is stale.
     *
     * Deduplication by _id happens here too: a notification arriving between two
     * requests shifts the page boundary, so page 2 can legitimately repeat a row
     * already shown on page 1.
     */
    applyFeedPage(state, { requestId, page, pages, docs, isLoadMore, isRefresh }) {
      if (state.feedRequestId !== requestId) {
        return state;
      }

      // Refresh puts the fresh page 1 on top and keeps the rows already loaded
      // below it. A plain replace would drop the pages the reader had scrolled
      // through -- the "rows must not disappear" case -- so the tail is kept and
      // deduped against the incoming page instead.
      const feed = isRefresh
        ? dedupeById(docs, state.feed)
        : isLoadMore
          ? dedupeById(state.feed, docs)
          : dedupeById([], docs);

      return {
        ...state,
        feed,
        // A refresh re-reads page 1 without unloading the later pages, so the
        // reader's position in the feed is unchanged.
        feedPage: isRefresh ? state.feedPage : page,
        feedPages: pages,
        isFeedLoading: false,
        isFeedLoadingMore: false,
        feedError: false,
      };
    },

    /** Same staleness guard as applyFeedPage: a request that failed after the
     * dropdown closed must not raise the error state for the next open. */
    applyFeedError(state, { requestId }) {
      if (state.feedRequestId !== requestId) {
        return state;
      }

      return { ...state, isFeedLoading: false, isFeedLoadingMore: false, feedError: true };
    },

    applyReadState(state, { ids } = {}) {
      const target = ids ? new Set(ids.map(String)) : null;
      let changed = false;

      const feed = state.feed.map(item => {
        if (item?.isNotificationRead === true || (target && !target.has(String(item?._id)))) {
          return item;
        }

        changed = true;

        return { ...item, isNotificationRead: true };
      });

      return changed ? { ...state, feed } : state;
    },

    applyAllReadState(state) {
      return {
        ...state,
        feed: state.feed.map(item => (item.isNotificationRead === true ? item : { ...item, isNotificationRead: true })),
        // Pending pages may contain unread values captured before mark-all.
        feedRequestId: state.feedRequestId + 1,
        isFeedLoading: false,
        isFeedLoadingMore: false,
      };
    },

    /**
     * Clears the feed when the dropdown closes, so reopening always starts from
     * a fresh page 1 rather than showing a stale list while the request is out.
     * Bumping feedRequestId is what makes any in-flight request a no-op.
     */
    /** Marks the tray as being looked at, so socket events refresh it. */
    openFeed(state) {
      return { ...state, isFeedOpen: true };
    },

    resetFeed(state) {
      return {
        ...state,
        feed: [],
        feedPage: 1,
        feedPages: 0,
        isFeedLoading: false,
        isFeedLoadingMore: false,
        feedError: false,
        isFeedOpen: false,
        feedRequestId: state.feedRequestId + 1,
      };
    },
  },
  effects: () => ({
    /**
     * Unread count for the bell badge.
     *
     * Resolves true when the badge is either up to date or about to be, and
     * false only when the fetch failed, so a caller holding the one attempt a
     * session gets can tell whether it was spent.
     */
    async getUnreadCount(payload, rootState) {
      // Read before the await, where the dispatch-time snapshot is the right
      // value: the flag below is set synchronously, so a second event dispatched
      // while the first request is out sees it and drops out.
      if (rootState?.notificationsModel?.isUnreadCountLoading) {
        this.setData({ unreadCountRefetchQueued: true });

        return true;
      }

      // Read before the request, so it names the session that asked for it.
      const identity = currentNotificationIdentity();

      this.setData({ isUnreadCountLoading: true, unreadCountRefetchQueued: false });

      try {
        const response = await notificationsService.fetchUnreadCount();

        // Reported as handled rather than failed: whoever is signed in now
        // has their own fetch, and a retry would only re-ask for a count that
        // belongs to nobody on screen.
        if (currentNotificationIdentity() !== identity) {
          return true;
        }

        const unreadCount = parseUnreadCount(response);

        if (unreadCount === null) {
          this.setData({ unreadCountError: true });

          return false;
        }

        this.setData({ unreadCount, unreadCountError: false });

        return true;
      } catch (error) {
        this.setData({ unreadCountError: true });

        return false;
      } finally {
        this.setData({ isUnreadCountLoading: false });
        await this.drainQueuedUnreadCount();
      }
    },

    /**
     * Runs the one refresh that was coalesced away, if any. Kept as its own
     * effect so its `rootState` is read at dispatch time -- i.e. after the
     * request that just finished wrote its flags -- rather than from the stale
     * snapshot the caller was holding across its await.
     */
    /**
     * Pulls a fresh page 1 into the open tray. Called from the socket handler,
     * so a notification arriving while someone has the tray open shows up
     * without them closing and reopening it.
     */
    async refreshOpenFeed(payload, rootState) {
      if (!rootState?.notificationsModel?.isFeedOpen) {
        return;
      }

      await this.getNotifications({ page: 1, isRefresh: true });
    },

    async drainQueuedUnreadCount(payload, rootState) {
      if (!rootState?.notificationsModel?.unreadCountRefetchQueued) {
        return;
      }

      this.setData({ unreadCountRefetchQueued: false });

      await this.getUnreadCount();
    },

    /**
     * A page of the notification feed for the bell dropdown.
     *
     * Page 1 replaces the list, later pages append. This effect only fetches;
     * the merge, the deduplication and the staleness check all live in the
     * applyFeedPage reducer, which can see current state.
     */
    async getNotifications(payload, rootState) {
      const page = Number.isInteger(payload?.page) && payload.page > 0 ? payload.page : 1;
      const isRefresh = payload?.isRefresh === true;
      const isLoadMore = !isRefresh && page > 1;
      // Both read before the await, so they name the request's own epoch and
      // the session that asked for it.
      const requestId = rootState?.notificationsModel?.feedRequestId ?? 0;
      const identity = currentNotificationIdentity();

      // A refresh happens under the reader's eyes, so it raises neither spinner:
      // the rows stay put and are replaced in place when the page lands.
      if (!isRefresh) {
        this.setData(isLoadMore ? { isFeedLoadingMore: true } : { isFeedLoading: true, feedError: false });
      }

      try {
        const response = await notificationsService.fetchNotifications({ page, limit: FEED_PAGE_SIZE });

        if (currentNotificationIdentity() !== identity) {
          return;
        }

        if (isFailedListResponse(response)) {
          this.applyFeedError({ requestId });

          return;
        }

        const { docs, pages } = parseNotificationList(response);

        this.applyFeedPage({ requestId, page, pages, docs, isLoadMore, isRefresh });
      } catch (error) {
        // A failure belonging to a session that has been replaced is not the
        // new session's error to show.
        if (currentNotificationIdentity() !== identity) {
          return;
        }

        this.applyFeedError({ requestId });
      }
    },

    async markAsRead(notificationId) {
      if (!notificationId) {
        return;
      }

      try {
        if (!isSuccessfulMutation(await notificationsService.markNotificationAsRead(notificationId))) {
          return;
        }

        this.applyReadState({ ids: [notificationId] });
        await this.getUnreadCount();
      } catch (error) {}
    },

    async markAllAsRead() {
      try {
        if (!isSuccessfulMutation(await notificationsService.markAllNotificationsAsRead())) {
          return;
        }

        this.applyAllReadState();
        await this.getUnreadCount();
      } catch (error) {}
    },
  }),
};

export default notificationsModel;
